<template>
  <v-card class="mt-6">
    <v-card-text class="pa-0">
      <div class="px-4 pt-4">
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('components.rowsPerPage.entries') }}
            </div>
            <v-select
              v-model="filters.itemsPerPage"
              :items="itemsPerPageOptions"
              :disabled="!transactions.length"
              solo
              hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('miscellaneous.startDate') }}
            </div>
            <v-text-field
              v-model="filters.createdFrom"
              type="date"
              solo
              hide-details
              clearable />
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('miscellaneous.endDate') }}
            </div>
            <v-text-field
              v-model="filters.createdUntil"
              type="date"
              solo
              hide-details
              clearable />
          </v-col>
        </v-row>
        <v-row dense class="mt-2">
          <v-col cols="12" md="4">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('entities.stockTransactions.transactionType') }}
            </div>
            <v-select
              v-model="filters.transactionType"
              :items="transactionTypeOptions"
              :disabled="!transactionTypes.length"
              item-text="text"
              item-value="value"
              solo
              hide-details
              clearable />
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('entities.products.variant') }}
            </div>
            <v-autocomplete
              v-model="filters.variantId"
              :items="variantOptions"
              :disabled="!variantOptions.length"
              item-text="text"
              item-value="value"
              solo
              hide-details
              clearable />
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('entities.users.entity') }}
            </div>
            <v-autocomplete
              v-model="filters.userId"
              :items="userOptions"
              :disabled="!userOptions.length"
              item-text="text"
              item-value="value"
              solo
              hide-details
              clearable />
          </v-col>
        </v-row>
      </div>
      <v-toolbar flat class="px-4 my-4">
        <v-toolbar-title>
          {{ $t('entities.stockTransactions.title') }}
        </v-toolbar-title>
        <v-divider class="mx-4" inset vertical />
        <v-spacer />
        <v-btn
          @click="handleToolbarAction('ADD')"
          color="primary"
          depressed
          class="mx-1">
          {{ $t('entities.stockTransactions.addStock') }}
        </v-btn>
        <v-btn
          @click="handleToolbarAction('REMOVE')"
          color="primary"
          depressed
          class="mx-1">
          {{ $t('entities.stockTransactions.removeStock') }}
        </v-btn>
        <v-btn
          @click="handleToolbarAction('WASTE')"
          color="primary"
          depressed
          class="mx-1">
          {{ $t('entities.stockTransactions.wasteStock') }}
        </v-btn>
        <v-btn
          @click="handleToolbarAction('RESOLVE_WASTE')"
          color="primary"
          depressed
          class="mx-1">
          {{ $t('entities.stockTransactions.resolveWaste') }}
        </v-btn>
      </v-toolbar>
      <v-simple-table class="mt-4">
        <thead>
          <tr>
            <th>{{ $t('entities.stockTransactions.transactionType') }}</th>
            <th>{{ $t('entities.products.variant') }}</th>
            <th>{{ $t('generalAttributes.quantity') }}</th>
            <th>{{ $t('entities.users.entity') }}</th>
            <th>{{ $t('entities.suppliers.entity') }}</th>
            <th>{{ $t('miscellaneous.description') }}</th>
            <th>{{ $t('miscellaneous.date') }}</th>
            <th class="text-left">{{ $t('miscellaneous.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="8" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" />
            </td>
          </tr>
          <tr v-else-if="!pagedTransactions.length">
            <td colspan="8" class="text-center grey--text py-6">
              {{ $t('components.noData.noEntriesFound') }}
            </td>
          </tr>
          <template v-else>
            <tr v-for="transaction in pagedTransactions" :key="transaction.id">
              <td>
                <v-chip
                  v-if="getTransactionTypeCode(transaction)"
                  small
                  :color="getTransactionTypeColor(transaction)">
                  {{ getTransactionTypeLabel(transaction) }}
                </v-chip>
                <span v-else>-</span>
              </td>
              <td>{{ getVariantLabel(transaction) }}</td>
              <td>{{ getTransactionQuantity(transaction) }}</td>
              <td>{{ getUserLabel(transaction) }}</td>
              <td>{{ getSupplierLabel(transaction) }}</td>
              <td>{{ transaction.description || '-' }}</td>
              <td>{{ getTransactionDate(transaction) }}</td>
              <td class="text-center">
                <div class="d-flex justify-start">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        depressed
                        x-small
                        fab
                        color="secondary"
                        v-bind="attrs"
                        v-on="on"
                        class="glow ml-2"
                        @click="openDetailsDialog(transaction)">
                        <v-icon small>pageview</v-icon>
                      </v-btn>
                    </template>
                    <span>{{
                      $t('entities.stockTransactions.viewTransaction')
                    }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        depressed
                        x-small
                        fab
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click="openEditTransactionDialog(transaction)"
                        class="ml-2">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('miscellaneous.edit') }}</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>
    </v-card-text>
    <v-divider />
    <v-card-actions class="justify-center">
      <div>
        <v-pagination
          v-model="page"
          :length="totalPages"
          :total-visible="5"
          :disabled="!filteredTransactions.length"
          color="primary" />
        <h5
          v-if="paginationSummary"
          class="card-title font-weight-light mt-2 text-center">
          {{ paginationSummary }}
        </h5>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import formatDate from '@/mixins/format-date'
import TRANSACTION_TYPE_COLORS from './colors'
import thousandSeparator from '@/mixins/thousand-separator'

export default {
  name: 'StockTransactionsTableList',
  mixins: [thousandSeparator, formatDate],
  props: {
    itemsPerPage: { type: Number, default: 10 },
    arenaVariants: { type: Array, default: () => [] },
    suppliers: { type: Array, default: () => [] },
    transactions: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    currentPage: { type: Number, default: 1 },
    totalTransactions: { type: Number, default: 0 },
    transactionTypes: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    initialVariantId: { type: String, default: null },
    initialCreatedFrom: { type: String, default: null },
    initialCreatedUntil: { type: String, default: null },
  },
  data() {
    return {
      filters: {
        itemsPerPage: this.itemsPerPage,
        transactionType: null,
        variantId: this.initialVariantId,
        userId: null,
        createdFrom: this.initialCreatedFrom,
        createdUntil: this.initialCreatedUntil,
      },
      page: this.currentPage || 1,
      isUpdatingPageFromParent: false,
      isUpdatingItemsPerPageFromParent: false,
    }
  },
  computed: {
    currentItemsPerPage() {
      const value = Number(this.filters.itemsPerPage)
      return !Number.isNaN(value) && value > 0 ? value : this.itemsPerPage
    },
    filteredTransactions() {
      if (!Array.isArray(this.transactions)) return []
      return this.transactions
    },
    paginationItemCount() {
      if (
        typeof this.totalTransactions === 'number' &&
        this.totalTransactions > 0
      ) {
        return this.totalTransactions
      }
      return this.filteredTransactions.length
    },
    totalPages() {
      if (!this.paginationItemCount) return 1
      return Math.max(
        1,
        Math.ceil(this.paginationItemCount / this.currentItemsPerPage),
      )
    },
    pagedTransactions() {
      if (!this.filteredTransactions.length) return []
      return this.filteredTransactions
    },
    paginationSummary() {
      const total = Number(this.totalTransactions) || 0
      if (!total) return ''
      const perPage = this.currentItemsPerPage
      const currentPage = Number(this.page) || 1
      const rawFrom = (currentPage - 1) * perPage + 1
      const rawTo = rawFrom + perPage - 1
      const boundedFrom = Math.min(rawFrom, total)
      const boundedTo = Math.min(rawTo, total)
      const formattedFrom = this.addThousandSeparator(boundedFrom)
      const formattedTo = this.addThousandSeparator(boundedTo)
      const formattedTotal = this.addThousandSeparator(total)
      return `${formattedFrom} - ${formattedTo} ${this.$t(
        'components.pagination.of',
      )} ${formattedTotal}`
    },
    itemsPerPageOptions() {
      const defaults = [10, 25, 50, 100]
      return defaults.includes(this.itemsPerPage)
        ? defaults
        : [this.itemsPerPage, ...defaults]
    },
    transactionTypeOptions() {
      const options = this.transactionTypes.map((it) => ({
        text: this.$t(`entities.stockTransactions.types.${it.code}`),
        value: it.id,
      }))
      return [
        {
          text: this.$t('components.rowsPerPage.all'),
          value: null,
        },
        ...options,
      ]
    },
    variantOptions() {
      const variants = Array.isArray(this.arenaVariants)
        ? this.arenaVariants
        : []
      const variantItems = variants
        .filter((variant) => variant?.id !== undefined && variant?.id !== null)
        .map((variant) => ({
          text: variant?.name || variant?.sku || String(variant?.id || ''),
          value: variant?.id,
        }))
      return [
        {
          text: this.$t('components.rowsPerPage.all'),
          value: null,
        },
        ...variantItems,
      ]
    },
    variantLabelMap() {
      if (!Array.isArray(this.arenaVariants)) return {}
      return this.arenaVariants.reduce((map, variant) => {
        if (variant?.id === undefined || variant?.id === null) return map
        map[String(variant.id)] = variant?.name || variant?.sku || variant.id
        return map
      }, {})
    },
    userLabelMap() {
      if (!Array.isArray(this.users)) return {}
      return this.users.reduce((acc, user) => {
        const key = String(user.id)
        const label = user?.name || user?.full_name || user?.username || '-'
        acc[key] = label
        return acc
      }, {})
    },
    userOptions() {
      const options = Array.isArray(this.users)
        ? this.users
            .filter((user) => user?.id !== undefined && user?.id !== null)
            .map((user) => ({
              text:
                user?.name ||
                user?.full_name ||
                user?.username ||
                String(user?.id),
              value: user.id,
            }))
        : []
      return [
        { text: this.$t('components.rowsPerPage.all'), value: null },
        ...options,
      ]
    },
  },
  methods: {
    handleToolbarAction(type) {
      this.$emit('transaction:open', type)
    },
    getActiveFilters() {
      return {
        transactionType: this.filters.transactionType,
        variantId: this.filters.variantId,
        userId: this.filters.userId,
        createdFrom: this.filters.createdFrom,
        createdUntil: this.filters.createdUntil,
      }
    },
    emitPaginationChange(pageOverride = this.page) {
      this.$emit('pagination:change', {
        page: pageOverride,
        itemsPerPage: this.currentItemsPerPage,
        filters: this.getActiveFilters(),
      })
    },
    setPageSilently(newPage) {
      if (this.page === newPage) return
      this.isUpdatingPageFromParent = true
      this.page = newPage
    },
    handleFilterChange() {
      this.setPageSilently(1)
      this.emitPaginationChange(1)
    },
    getTransactionTypeCode(transaction) {
      return transaction?.type?.code
    },
    getTransactionTypeLabel(transaction) {
      const code = this.getTransactionTypeCode(transaction)
      if (!code) return '-'
      return this.$t(`entities.stockTransactions.types.${code}`)
    },
    getTransactionTypeColor(transaction) {
      const code = this.getTransactionTypeCode(transaction)
      return code
        ? TRANSACTION_TYPE_COLORS[code]
        : TRANSACTION_TYPE_COLORS.DEFAULT
    },
    getSupplierLabel(transaction) {
      const fallback = '-'
      const supplierId = transaction.supplier_id
      if (!supplierId) return fallback
      const supplierFromList = this.suppliers.find((it) => it.id === supplierId)
      return supplierFromList?.name || fallback
    },
    getUserLabel(transaction) {
      const userId = String(transaction.user_id)
      return this.userLabelMap[userId] || '-'
    },
    getVariantId(transaction) {
      return (
        transaction?.arena_variant?.id ||
        transaction?.arena_variant_id ||
        transaction?.variant_id ||
        null
      )
    },
    getVariantLabel(transaction) {
      const directLabel =
        transaction?.arena_variant?.name || transaction?.variant_name || null
      if (directLabel) return directLabel
      const variantId = this.getVariantId(transaction)
      if (variantId && this.variantLabelMap[String(variantId)]) {
        return this.variantLabelMap[String(variantId)]
      }
      return '-'
    },
    getTransactionQuantity(transaction) {
      const typeCode = this.getTransactionTypeCode(transaction)
      const rawDiff =
        typeCode === 'WASTE' || typeCode === 'RESOLVE_WASTE'
          ? transaction.waste_stock_after - transaction.waste_stock_before
          : transaction.available_stock_after -
            transaction.available_stock_before
      const normalizedDiff = Math.abs(rawDiff)
      return this.addThousandSeparator(normalizedDiff)
    },
    getTransactionDate(transaction) {
      const parsedDate = new Date(transaction.created_at)
      return this.formatDate(parsedDate, true, true)
    },
    openEditTransactionDialog(transaction) {
      this.$emit('transaction:edit', transaction)
    },
    openDetailsDialog(transaction) {
      this.$emit('transaction:view', transaction)
    },
  },
  watch: {
    transactions: {
      handler() {
        if (this.page > this.totalPages) {
          this.setPageSilently(this.totalPages)
        }
      },
      deep: true,
      immediate: true,
    },
    currentPage(value) {
      const normalized = Number(value) || 1
      this.setPageSilently(normalized)
    },
    page(value, oldValue) {
      if (value === oldValue) return
      if (value < 1) {
        this.page = 1
        return
      }
      if (this.isUpdatingPageFromParent) {
        this.isUpdatingPageFromParent = false
        return
      }
      this.emitPaginationChange(value)
    },
    itemsPerPage(value) {
      if (!value || value === this.filters.itemsPerPage) return
      this.isUpdatingItemsPerPageFromParent = true
      this.filters.itemsPerPage = value
    },
    'filters.itemsPerPage'(newValue, oldValue) {
      if (newValue === oldValue) return
      this.setPageSilently(1)
      if (this.isUpdatingItemsPerPageFromParent) {
        this.isUpdatingItemsPerPageFromParent = false
        return
      }
      this.emitPaginationChange(1)
    },
    'filters.transactionType'() {
      this.handleFilterChange()
    },
    'filters.variantId'() {
      this.handleFilterChange()
    },
    'filters.userId'() {
      this.handleFilterChange()
    },
    'filters.createdFrom'() {
      this.handleFilterChange()
    },
    'filters.createdUntil'() {
      this.handleFilterChange()
    },
  },
}
</script>
